// Utilities for proprietary player valuation formula
import type { Player } from '../types/player';

// Define valid fantasy positions to improve type checking
export type FantasyPosition = 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DEF' | 'DST' | 'D/ST';

/** 
 * Centralized function to determine if a player is valid for fantasy football
 * This ensures consistent filtering across the entire application
 */
export function isValidFantasyPlayer(player: any, includeDefenses: boolean = true): boolean {
  // Filter out all defensive players (individual defensive positions)
  const defensivePositions = ['CB', 'S', 'FS', 'SS', 'LB', 'ILB', 'OLB', 'MLB', 'DE', 'DT', 'NT', 'DL', 'EDGE'];
  if (defensivePositions.includes(player.position)) {
    return false;
  }
  
  // Filter out offensive line players (not used in standard fantasy)
  const offensiveLinePositions = ['OL', 'C', 'G', 'LG', 'RG', 'T', 'LT', 'RT', 'OT'];
  if (offensiveLinePositions.includes(player.position)) {
    return false;
  }
  
  // Handle team defenses based on context
  const teamDefensePositions = ['DEF', 'DST', 'D/ST'];
  if (teamDefensePositions.includes(player.position)) {
    return includeDefenses; // Only include if specifically requested
  }
  
  // Only allow standard fantasy positions
  const validFantasyPositions = ['QB', 'RB', 'WR', 'TE', 'K'];
  if (!validFantasyPositions.includes(player.position)) {
    return false;
  }
  
  // Filter out inactive players (but be more lenient - only filter if explicitly inactive)
  if (player.isActive === false || player.active === false) {
    return false;
  }
  
  // Filter out players with no team (free agents, retired, etc.) - but be more lenient
  if (!player.team) {
    return false;
  }
  
  return true;
}

/** 
 * Filter an array of players and log summary statistics
 */
export function filterPlayersWithSummary(players: any[], context: string = 'Unknown', includeDefenses: boolean = true): any[] {
  const totalPlayers = players.length;
  const validPlayers = players.filter(player => isValidFantasyPlayer(player, includeDefenses));
  const filteredCount = totalPlayers - validPlayers.length;
  
  const defenseNote = includeDefenses ? " (including team defenses)" : " (excluding team defenses)";
  console.log(`${context}: Filtered ${filteredCount} non-fantasy players from ${totalPlayers} total players. ${validPlayers.length} fantasy players remaining${defenseNote}.`);
  
  return validPlayers;
}

/** League settings defining roster size and starting slots */
export interface LeagueSettings {
  leagueSize: number;
  startingSlots: Record<string, number>;
}

// New types for enhanced factors
export interface TeamOffensiveStats {
  playsPerGame: number;
  pointsPerGame: number;
  redZoneScoreRate: number;
  offensiveLineRank: number;
  totalTargets: number;
  totalCarries: number;
  totalRedZoneOpportunities: number;
  totalAirYards: number;
  winPercentage: number;
  avgTimeOfPossession: number;
}

export interface LeagueAverages {
  playsPerGame: number;
  pointsPerGame: number;
  redZoneScoreRate: number;
}

export interface PlayerStats {
  targets: number;
  carries: number;
  redZoneOpportunities: number;
  airYards: number;
  schedule: string[];
}

export interface TeamStats extends TeamOffensiveStats {
  // Additional team stats if needed
}

export interface InjuryHistory {
  type: string;
  gamesMissed: number;
  season: number;
}

/** Enhanced formula weights */
const FORMULA_WEIGHTS = {
  ageFactor: 0.12,           // Age sustainability
  positionScarcity: 0.15,    // Position scarcity
  previousPPG: 0.18,         // Proven production
  projectedPoints: 0.20,     // Future outlook
  teamNeedsFactor: 0.08,     // Team fit
  vorp: 0.04,               // Replacement value
  teamOffensiveStrength: 0.08, // Team offensive context
  targetTouchShare: 0.06,    // Opportunity quality
  airYardsShare: 0.04,      // Target quality
  defensiveGameScript: 0.03, // Game situation impact
  injuryRiskFactor: 0.02    // Injury risk adjustment
};

/** Normalize age into 0–1 based on peak and decline */
export function calculateAgeFactor(age: number): number {
  const peakAge = 27;
  const maxAge = 35;
  const minAge = 20;
  if (age <= peakAge) {
    return 0.3 + (0.7 * ((age - minAge) / (peakAge - minAge)));
  } else {
    const decline = Math.pow((age - peakAge) / (maxAge - peakAge), 1.5);
    return Math.max(0.1, 1.0 - (0.6 * decline));
  }
}

/** Calculate scarcity multiplier based on league depth */
export function calculatePositionScarcity(
  position: string,
  leagueSettings: LeagueSettings
): number {
  const slots = leagueSettings.startingSlots[position] || 1;
  const base = 1 / (leagueSettings.leagueSize * slots);
  const multipliers: Record<string, number> = {
    QB: 1.2, RB: 1.8, WR: 1.0, TE: 1.5, K: 0.3, DST: 0.4, DEF: 0.4
  };
  return base * (multipliers[position] || 1.0);
}

/** Normalize previous PPG into 0–1 (assumes max ~30 ppg) */
export function calculatePreviousPPG(prev: number): number {
  return Math.max(0, Math.min(1, prev / 30));
}

/** Normalize projected points into 0–1 (assumes max ~300 total season pts) */
export function calculateProjectedPoints(proj: number): number {
  return Math.max(0, Math.min(1, proj / 300));
}

/** Weight based on team roster needs for a given position */
export function calculateTeamNeedsFactor(
  position: string,
  roster: Player[],
  leagueSettings: LeagueSettings
): number {
  const depth = roster.filter(p => p.position === position).length;
  const slots = leagueSettings.startingSlots[position] || 1;
  if (depth < slots) return 1.0;
  if (depth === slots) return 0.8;
  return Math.max(0.1, 0.4 - 0.1 * (depth - slots));
}

/** Value Over Replacement Player normalized */
export function calculateVORP(
  score: number,
  replacementScore: number
): number {
  const vorp = (score - replacementScore) / Math.max(1, replacementScore);
  return Math.max(0, Math.min(1, vorp / 2));
}

// Get average opposing defense rank from schedule  
export function getAverageOpposingDefenseRank(schedule?: any[], position?: string): number {
  // Fallback to middle-of-pack rank (16) if no schedule data
  if (!schedule || schedule.length === 0) return 16;
  
  // Mock implementation - in a real app, this would lookup actual defense rankings
  // For now, return a reasonable average with some position-specific variation
  const baseRank = 16;
  const positionModifier: Record<string, number> = {
    QB: -2,  // QBs face slightly easier defenses on average
    RB: 1,   // RBs face slightly tougher run defenses  
    WR: 0,   // Neutral
    TE: -1,  // TEs face slightly easier coverage
    K: 0,    // Neutral
    DEF: 0   // Neutral
  };
  
  return Math.max(1, Math.min(32, baseRank + (positionModifier[position || 'WR'] || 0)));
}

/** Team-level offensive context factor */
export function calculateTeamOffensiveStrength(
  teamStats?: TeamOffensiveStats,
  leagueAverages?: LeagueAverages
): number {
  if (!teamStats || !leagueAverages) return 1; // Default to neutral if data is missing

  // Prevent division by zero by checking league average denominators
  const pace = leagueAverages.playsPerGame > 0 ? teamStats.playsPerGame / leagueAverages.playsPerGame : 1;
  const eff = leagueAverages.pointsPerGame > 0 ? teamStats.pointsPerGame / leagueAverages.pointsPerGame : 1;
  const rz = leagueAverages.redZoneScoreRate > 0 ? teamStats.redZoneScoreRate / leagueAverages.redZoneScoreRate : 1;
  const ol = (33 - (teamStats.offensiveLineRank || 16)) / 32; // Default OL rank to 16 if undefined
  return 0.3 * pace + 0.4 * eff + 0.2 * rz + 0.1 * ol;
}

/** Calculate target/touch share factor */
export function calculateTargetTouchShare(
  playerStats?: PlayerStats,
  teamStats?: TeamStats,
  position?: string
): number {
  if (!playerStats || !teamStats || !position) return 0.5;
  if (position === 'QB') return 1.0; // QBs get max share by nature
  
  const targetShare = teamStats.totalTargets > 0 ? playerStats.targets / teamStats.totalTargets : 0;
  const carryShare = teamStats.totalCarries > 0 ? playerStats.carries / teamStats.totalCarries : 0;
  const redZoneShare = teamStats.totalRedZoneOpportunities > 0 ? 
    playerStats.redZoneOpportunities / teamStats.totalRedZoneOpportunities : 0;
  
  const positionWeights: Record<string, { targets: number; carries: number; redZone: number }> = {
    RB: { targets: 0.3, carries: 0.5, redZone: 0.2 },
    WR: { targets: 0.7, carries: 0.1, redZone: 0.2 },
    TE: { targets: 0.6, carries: 0.1, redZone: 0.3 }
  };
  
  const weights = positionWeights[position] || { targets: 0.5, carries: 0.3, redZone: 0.2 };
  return (targetShare * weights.targets) + 
         (carryShare * weights.carries) + 
         (redZoneShare * weights.redZone);
}

/** Calculate air yards share factor */
export function calculateAirYardsShare(
  playerStats?: PlayerStats,
  teamStats?: TeamStats,
  position?: string
): number {
  if (!playerStats || !teamStats || !position) return 0.5;
  if (['RB', 'QB'].includes(position)) return 0.5; // Neutral for non-receiving positions
  
  const airYardsShare = teamStats.totalAirYards > 0 ? 
    playerStats.airYards / teamStats.totalAirYards : 0;
  const averageDepthOfTarget = playerStats.targets > 0 ? 
    playerStats.airYards / playerStats.targets : 0;
  const leagueAvgDepth = 8.5; // NFL average
  
  const depthFactor = Math.min(1.5, averageDepthOfTarget / leagueAvgDepth);
  return Math.min(1.0, airYardsShare * depthFactor);
}

/** Situational defensive game script impact */
export function calculateDefensiveGameScript(
  playerStats?: PlayerStats,
  teamStats?: TeamOffensiveStats,
  position?: string
): number {
  if (!playerStats || !teamStats || !position) return 0.5;
  
  // Calculate pace tendency - higher pace = more opportunities
  const leagueAvgPace = 65; // Average NFL plays per game
  const paceFactor = teamStats.playsPerGame / leagueAvgPace;
  
  // Calculate game script tendency based on team success
  const gameScriptFactor = teamStats.winPercentage || 0.5; // Default to .500 if no data
  
  const oppRank = getAverageOpposingDefenseRank(playerStats.schedule, position);
  const adj = (33 - oppRank) / 32;
  
  // Define impact values for each position based on pace and game script
  const impactMap: Record<string, number> = {
    QB: teamStats.pointsPerGame > 25 ? 0.9 : 1.1, // Worse when ahead (less passing)
    RB: paceFactor * (gameScriptFactor > 0.6 ? 1.2 : 0.9), // Better with pace and when winning (running clock)
    WR: paceFactor * (gameScriptFactor < 0.4 ? 1.2 : 1.0), // Better with pace and when behind (passing)
    TE: paceFactor, // Neutral but benefits from pace
    K: 0.8 * paceFactor, // Slightly benefits from pace
    DEF: 0.8,
    DST: 0.8
  };
  
  // Get impact for position with fallback
  const impact = impactMap[position] || 0.8;
  
  return Math.min(1, adj * impact);
}

/** Calculate injury risk factor */
export function calculateInjuryRisk(
  player: Player,
  injuryHistory?: InjuryHistory[]
): number {
  const positionRiskMultiplier: Record<string, number> = {
    QB: 0.8,  // Lower injury risk
    RB: 1.3,  // Highest injury risk
    WR: 1.0,  // Baseline
    TE: 1.1,  // Slightly higher
    K: 0.7,   // Very low risk
    DEF: 0.7  // Very low risk
  };
  
  const ageRiskFactor = (player.age ?? 27) > 30 ? 1.2 : 
                       (player.age ?? 27) < 24 ? 0.9 : 1.0;
  
  const historyFactor = injuryHistory && injuryHistory.length > 2 ? 1.3 :
                       injuryHistory && injuryHistory.length > 0 ? 1.1 : 1.0;
  
  const riskScore = (positionRiskMultiplier[player.position] || 1.0) * 
                   ageRiskFactor * historyFactor;
  
  // Convert to value factor (lower risk = higher value)
  return Math.max(0.3, 1.5 - (riskScore - 1.0));
}

/** Composite overall score and component breakdown */
export function calculateOverallPlayerScore(
  player: Player & { 
    age?: number; 
              lastYearPoints?: number; 
    projection?: number; 
    teamStats?: TeamOffensiveStats; 
    leagueAverages?: LeagueAverages; 
    playerStats?: PlayerStats; 
    injuryHistory?: InjuryHistory[] 
  },
  leagueSettings: LeagueSettings,
  roster: Player[] = [],
  replacementScore: number = 0
): {
  overallScore: number;
  components: {
    ageFactor: number;
    positionScarcity: number;
          lastYearPoints: number;
    projectedPoints: number;
    teamNeedsFactor: number;
    vorp: number;
    teamOffensiveStrength: number;
    targetTouchShare: number;
    airYardsShare: number;
    defensiveGameScript: number;
    injuryRiskFactor: number;
  };
} {
  const ageFactor = calculateAgeFactor(player.age || 27);
  const positionScarcity = calculatePositionScarcity(player.position, leagueSettings);
  const prevPPG = calculatePreviousPPG(player.lastYearPoints || 0);
  const projPts = calculateProjectedPoints(player.projection || 0);
  const teamNeeds = calculateTeamNeedsFactor(player.position, roster, leagueSettings);
  const vorp = calculateVORP(player.projection || 0, replacementScore);
  const teamOffStr = calculateTeamOffensiveStrength(player.teamStats, player.leagueAverages);
  const targetShare = calculateTargetTouchShare(player.playerStats, player.teamStats, player.position);
  const airShare = calculateAirYardsShare(player.playerStats, player.teamStats, player.position);
  const defScript = calculateDefensiveGameScript(player.playerStats, player.teamStats, player.position);
  const injuryRisk = calculateInjuryRisk(player, player.injuryHistory);

  const overallScore = (
    FORMULA_WEIGHTS.ageFactor * ageFactor +
    FORMULA_WEIGHTS.positionScarcity * positionScarcity +
    FORMULA_WEIGHTS.previousPPG * prevPPG +
    FORMULA_WEIGHTS.projectedPoints * projPts +
    FORMULA_WEIGHTS.teamNeedsFactor * teamNeeds +
    FORMULA_WEIGHTS.vorp * vorp +
    FORMULA_WEIGHTS.teamOffensiveStrength * teamOffStr +
    FORMULA_WEIGHTS.targetTouchShare * targetShare +
    FORMULA_WEIGHTS.airYardsShare * airShare +
    FORMULA_WEIGHTS.defensiveGameScript * defScript +
    FORMULA_WEIGHTS.injuryRiskFactor * injuryRisk
  ) * 10;

  // Ensure we never return invalid scores
  const validScore = isNaN(overallScore) ? (projPts * 10) : overallScore;
  
  return {
    overallScore: validScore,
    components: {
      ageFactor,
      positionScarcity,
      lastYearPoints: prevPPG,
      projectedPoints: projPts,
      teamNeedsFactor: teamNeeds,
      vorp,
      teamOffensiveStrength: teamOffStr,
      targetTouchShare: targetShare,
      airYardsShare: airShare,
      defensiveGameScript: defScript,
      injuryRiskFactor: injuryRisk
    }
  };
}
